import React, { useEffect, useState, InputHTMLAttributes } from 'react';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { InputAdornment, TextField, Button, ButtonGroup } from '@mui/material';
import MonetizationOn from '@mui/icons-material/MonetizationOn';
import TranslateIcon from '@mui/icons-material/Translate';
import SaveIcon from '@mui/icons-material/Save';
import { QuestionHelp, QuestionHelpTypes } from '@beable/content-types';
import { Merchandise } from '@beable/roster-backend-client-v1';
import { getStyles } from './styles';

export interface QuestionHelpRow {
    type: QuestionHelpTypes;
    price: number;
    descriptionEn: string;
    descriptionEs: string;
}

const RowTextField = ({
    value, onChange, icon, multiline = false, type
}: {
    field: QuestionHelpRowKeys;
    value: unknown;
    icon: React.ReactNode;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    multiline?: boolean;
    type?: InputHTMLAttributes<unknown>['type'];
}) => {
    return (
        <TextField 
            value={value}
            variant="outlined"
            fullWidth={false}
            size='small'
            type={type}
            onChange={onChange}
            onFocus={(event) => event.target.select()}
            slotProps={{
                input: {
                  readOnly: false,
                  multiline,
                    startAdornment: 
                  <InputAdornment position="start">
                  { icon }
                  </InputAdornment>
                }
            }}
      />  
    )
};

type QuestionHelpRowKeys = keyof QuestionHelpRow;

interface QuestionHelpPricingTableProps {
    merchandise: Merchandise[];
    onSave: (rows: QuestionHelpRow[]) => void;
}

const defaultQuestionHelpPrice = 0;

export default function QuestionHelpPricingTable({ merchandise, onSave }: QuestionHelpPricingTableProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [ tableRows, setTableRows ] = useState<QuestionHelpRow[]>([]);
  const [ recordsModified, setModified ] = useState(false);

  /**
   * By iterating over `QuestionHelp` we ensure that the table is always populated
   * with the list of Question Help Types that are defined in the system even if `merchandise` records 
   * do not exist yet.
   */
  useEffect(() => {
    const pricingToDisplay: QuestionHelpRow[] = Object.values(QuestionHelp).map(helpType => {
        const existingMerch = merchandise.find( existing => (existing.name as QuestionHelp) === helpType);
        return {
            type: helpType,
            price: existingMerch?.price || defaultQuestionHelpPrice,
            descriptionEn: existingMerch?.description_en || helpType,
            descriptionEs: existingMerch?.description_es || helpType
            
        }
    });
    setTableRows(pricingToDisplay);
    setModified(false);

  }, [merchandise]);

  /**
   * Update the Table's state when a TextField is changed
   */
  const handleChange = (
        questionHelpType: QuestionHelpTypes, 
        prop: QuestionHelpRowKeys
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setModified(true);
        setTableRows(
            tableRows.map(row => { 
                if(row.type === questionHelpType) {
                    return { 
                        ...row, 
                        [prop]: event.target.value
                    };
                }
                return row;
            })
        );
    };
  return (
    <>
    <TableContainer sx={styles.pricingContainer}>
      <Table sx={styles.table} aria-label="question help pricing">
        <TableHead>
          <TableRow>
            <TableCell sx={styles.tableCell}>Question Help</TableCell>
            <TableCell sx={styles.tableCell}>Beable Buck Cost</TableCell>
            <TableCell sx={styles.tableCell}>Description (en)</TableCell>
            <TableCell sx={styles.tableCell}>Description (es)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map(({ type, price, descriptionEn, descriptionEs }) => (
            <TableRow key={type} data-cy={`table-row-${type}`}>
              <TableCell component="td" scope="row" sx={styles.tableCell}>
                <pre>{type}</pre>
            </TableCell>
            <TableCell component="td" scope="row" sx={styles.tableCell}>
                <RowTextField
                  value={price}
                  type='number'
                  field='price'
                  icon={<MonetizationOn />}
                  onChange={handleChange(type, 'price')}
                />
              </TableCell>
              <TableCell component="td" scope="row" sx={styles.tableCell}>
                <RowTextField
                  value={descriptionEn || type}
                  field='descriptionEn'
                  icon={<TranslateIcon />}
                  multiline={true}
                  onChange={handleChange(type, 'descriptionEn')}
                />
              </TableCell>
              <TableCell component="td" scope="row" sx={styles.tableCell}>
                <RowTextField
                  value={descriptionEs || type}
                  field='descriptionEs'
                  icon={<TranslateIcon />}
                  multiline={true}
                  onChange={handleChange(type, 'descriptionEs')}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
     <ButtonGroup color="primary" sx={styles.actions}>
        <Button 
            color='primary'     
            variant='contained'
            onClick={() => { onSave(tableRows); }}
            disabled={!recordsModified}
            startIcon={<SaveIcon/>}
            data-cy="save-pricing-button"
        >
            Save Question Help Pricing
        </Button>
    </ButtonGroup>
    
    </>
  );
}
